import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Edit2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useChatStore } from "../lib/useChatStore";
import { listConversations, createConversation, deleteConversation, loadMessages, renameConversation } from "../lib/api";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface SidebarProps {
  onConversationSelect?: () => void;
}

const Sidebar = ({ onConversationSelect }: SidebarProps = {}) => {
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const navigate = useNavigate();
  const { conversations, setConversations: setStoreConversations, activeConversationId, setActiveConversation, setMessages, clearMessages } = useChatStore();

  useEffect(() => {
    loadConversationsList();
  }, []);

  const loadConversationsList = async () => {
    const convs = await listConversations();
    setStoreConversations(convs);
  };

  const handleNewConversation = async () => {
    const newConv = await createConversation();
    setStoreConversations((prev) => [newConv, ...prev]);
    setActiveConversation(newConv.id);
    clearMessages();
    navigate(`/chat-juridico/${newConv.id}`);
  };

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConversationToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (conversationToDelete) {
      try {
        await deleteConversation(conversationToDelete);
        setStoreConversations((prev) => prev.filter((c) => c.id !== conversationToDelete));
        if (activeConversationId === conversationToDelete) {
          navigate("/chat-juridico");
        }
      } catch (error) {
        console.error("Erro ao excluir conversa:", error);
      } finally {
        setDeleteDialogOpen(false);
        setConversationToDelete(null);
      }
    }
  };

  const handleStartEdit = (id: string, title: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(id);
    setEditingTitle(title);
  };

  const handleSaveEdit = async (id: string) => {
    if (editingTitle.trim()) {
      await renameConversation(id, editingTitle.trim());
      setStoreConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, title: editingTitle.trim() } : c))
      );
    }
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingTitle("");
  };

  const filtered = conversations.filter((c) =>
    c.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full p-3 md:p-4 pt-14 gap-4 bg-muted/30" data-tour="conversation-sidebar">
      <Button onClick={handleNewConversation} className="w-full rounded-xl" data-tour="new-conversation-btn">
        <Plus className="mr-2 h-4 w-4" /> Nova Conversa
      </Button>

      <Input
        placeholder="Buscar conversas..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="rounded-xl"
      />

      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">Conversas</p>
      <div className="flex-1 overflow-y-auto space-y-1">
        {filtered.map((conv) => (
          <div
            key={conv.id}
            className={`group p-2 md:p-3 rounded-xl cursor-pointer transition-all border ${
              activeConversationId === conv.id
                ? "bg-accent border-border"
                : "bg-transparent hover:bg-accent/50 border-transparent"
            }`}
            onClick={async () => {
              if (editingId === conv.id) return;
              setActiveConversation(conv.id);
              const msgs = await loadMessages(conv.id);
              setMessages(msgs);
              navigate(`/chat-juridico/${conv.id}`);
              onConversationSelect?.(); // Fechar drawer mobile
            }}
          >
            <div className="flex items-start justify-between gap-2">
              {editingId === conv.id ? (
                <div className="flex-1 flex items-center gap-1">
                  <Input
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    className="h-7 text-sm"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveEdit(conv.id);
                      if (e.key === "Escape") handleCancelEdit();
                    }}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => handleSaveEdit(conv.id)}
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={handleCancelEdit}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{conv.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(conv.updated_at), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => handleStartEdit(conv.id, conv.title, e)}
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={(e) => handleDelete(conv.id, e)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="flex items-center justify-center h-full text-center text-muted-foreground">
            <p className="text-sm">Nenhuma conversa encontrada</p>
          </div>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir conversa</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta conversa? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Sidebar;
